'use client';

// «СПРАВКА» И «КОНФИДЕНЦИАЛЬНОСТЬ» СЛОЯМИ НА ТЕЛЕФОНЕ (ТЗ ANIM-FIX О5 П2, претензия
// владельца 04.10.2026, видео: «Справка без анимации» — строка уводила на /ru/help полной
// навигацией, менялась адресная строка Safari, хода не было). В браузере на телефоне
// справка встаёт слоем кабинета, как в iOS-приложении (APP-CLAIMS-1020 ред. 11, Д10 п.10.2):
// корень и раздел — боковые уровни («‹», свайп назад), конечная статья и документы
// («Конфиденциальность», «Соглашение») — стопкой снизу с «×». Вид уровня — `helpDir` ниже.
//
// СОДЕРЖИМОЕ — ТО ЖЕ, ЧТО У СТРАНИЦ /help: тексты читает ручка /api/help (файлы
// content/help/**, второго разбора нет), рисуют те же части справки — каркас LegalShell
// (голый: без шапки сайта и подвала), значки разделов, HelpMarkdown, «Поделиться» — и те же
// телефонные правила help.css. Ссылки на /help/... внутри слоя открывают следующий уровень,
// «Поделиться» отдаёт публичный адрес статьи.
//
// АДРЕС: /app/account/help (по образцу подстраниц «Аккаунта», accountHrefOf), раздел и статья —
// /app/account/help/<раздел>[/<статья>], документы — …/help/privacy и …/help/terms: «назад»
// браузера снимает уровень тем же ходом (popstate → leaveLevel). Прямой заход по такому адресу
// открывает справку и тот же уровень. В приложении — как было (родные экраны / лист `?bare=1`).
//
// ШИРОКИЙ ЭКРАН (три слоя, ТЗ ANIM-FIX О6, претензия владельца 04.10.2026 «весь раздел справка не
// работает»: в правом поле стояла пилюля-запаска HelpRowBody): те же тела рисует HelpPane в правом
// поле «Аккаунта» — уровень решает АДРЕС (те же /app/account/help/…), шаг вглубь кладёт запись
// истории (тихий pushState, как вкладки кабинета), «назад» браузера и «‹» шапки поля снимают
// уровень. Канон: на широком экране пункт колонки открывает содержимое СПРАВА, шторок нет
// (UI-TEMPLATE-CANON, «Фундамент дизайна» п.2). Куда ведёт шаг, тела узнают из HelpNavContext:
// в поле его ставит HelpPane, без него — уровни бокового слоя.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { KBareButton } from '@scriptera/ui/kit';
import { LegalShell } from '@/components/legal/legal-shell';
import { PrivacyText, privacyHead } from '@/components/legal/privacy-text';
import { TermsText, termsHead } from '@/components/legal/terms-text';
import { HelpArticle } from '@/components/help/help-article';
import { HelpSectionIcon } from '@/components/help/help-section-icon';
import { HelpShare } from '@/components/help/help-share';
import { HelpSearch, HelpSearchButton, askHelpSearch } from '@/components/help/help-search';
import { HelpBackPill } from '@/components/help/help-top';
import { pickToPath, tailOf } from '@/lib/cabinet-urls';
import {
  cardTitles,
  articleOf,
  helpIndexKey,
  helpIndexOpts,
  helpIndexUrl,
  type HelpIndexReply,
} from '@/lib/help-article-data';
import { articleTitles } from '@/lib/help-titles';
import { usePersistentData } from '@/lib/use-persistent-data';
import { awaitPersistent } from '@/lib/cabinet-warm';
import { useSlidePanel } from './native-layer-host';
import { useHelpSectionSeed } from './help-seed';
import { Shape, useShapeRecord } from './skeleton';
import { isAppClient, readLayoutMode } from './layout-mode';
import '@/components/help/help.css';

const HELP_BASE = pickToPath('account', 'help');
const secHref = (section: string): string => `${HELP_BASE}/${section}`;
const artHref = (section: string, slug: string): string => `${HELP_BASE}/${section}/${slug}`;

// Вся справка — заранее, на входе в кабинет: цель `help.index` плана прогрева (У1, lib/app-pages/warm-plan.ts).
function useHelpIndex(locale: string) {
  return usePersistentData<HelpIndexReply>(helpIndexKey(locale), helpIndexUrl(locale), helpIndexOpts);
}

/** Куда ведёт шаг справки: следующий уровень бокового слоя (телефон, панель-телефон) или
 *  следующая страница правого поля (широкий экран, HelpPane). */
interface HelpNav {
  openSection: (slug: string) => void;
  openArticle: (section: string, slug: string) => void;
  openPrivacy: () => void;
  openTerms: () => void;
}
const HelpNavContext = createContext<HelpNav | null>(null);

/** Слово пилюли «‹ Справка». */
function HelpTitleWord() {
  const t = useTranslations('help');
  return <>{t('title')}</>;
}

/** Пилюля «‹ куда» в левом слоте шапки листа справки. */
function HelpBackSlot({ label, onBack }: { label: ReactNode; onBack: () => void }) {
  return <HelpBackPill label={label} onClick={onBack} />;
}

/** ВИД УРОВНЯ СПРАВКИ — КАК НА ТЕЛЕФОНЕ (APP-CLAIMS-1020 ред. 11, Д10 п.10.2; решения владельца 13.1 и 17.11,
 *  слово 21.10.2026 20:07 «открываться должно так же»; страницы сервера help.ts: разделы `present: 'push'`,
 *  статьи и документы `present: 'deck'`). Телефон в браузере: корень из «Аккаунта» и РАЗДЕЛ — БОКОВЫЕ уровни
 *  («‹», свайп назад); КОНЕЧНАЯ СТАТЬЯ, политика и соглашение — СТОПКОЙ снизу с «×» (колода, ручка, тень —
 *  закон хоста, ТЗ WEB-CAUSES М87/М92). Прежняя стопка всех уровней (приказ 04.10.2026, Закон 5 п.1) заменена
 *  словом владельца 21.10 — абзац CLAUDE.md правлен тем же коммитом. Панель-телефон на широком экране
 *  («два слоя») — как было, боковыми уровнями. */
function helpDir(kind: 'page' | 'leaf'): { sheet: true; lead: 'close' } | { side: true } {
  if (kind === 'leaf' && !isAppClient() && readLayoutMode() === 'phone') return { sheet: true, lead: 'close' };
  return { side: true };
}

/** Открыватели уровней слоя справки: стопкой с именем поверх корня. */
function useLayerNav(): HelpNav {
  const panel = useSlidePanel();
  const locale = useLocale();
  // ПИЛЮЛЯ «‹ куда» ВМЕСТО КРУЖКА «‹» (пакет М круг 2, кадр iOS K17-help-section «‹ Справка»): только
  // боковой уровень раздела на телефоне; снимает один уровень. У статьи пилюли больше нет — она стопка с «×» (10.2).
  const back = useCallback(
    (label: ReactNode) =>
      !isAppClient() && readLayoutMode() === 'phone' ? { headerLeft: <HelpBackSlot label={label} onBack={() => panel.close()} /> } : {},
    [panel],
  );
  const openSection = useCallback(
    (slug: string) =>
      panel.open({
        title: '',
        ...helpDir('page'),
        ...back(<HelpTitleWord />),
        winKey: 'help-sec',
        href: secHref(slug),
        body: <HelpSectionBody slug={slug} />,
      }),
    [panel, back],
  );
  // Статья — окном-стопкой поверх справки (общий открыватель ниже); адрес уровня — /app/account/help/<раздел>/<статья>.
  const openArticle = useCallback(
    (section: string, slug: string) => openArticleSheet(panel, locale, { section, slug, href: artHref(section, slug) }),
    [panel, locale],
  );
  const openPrivacy = useCallback(
    () => panel.open({ title: '', ...helpDir('leaf'), winKey: 'help-privacy', href: `${HELP_BASE}/privacy`, body: <PrivacyBody /> }),
    [panel],
  );
  const openTerms = useCallback(
    () => panel.open({ title: '', ...helpDir('leaf'), winKey: 'help-terms', href: `${HELP_BASE}/terms`, body: <TermsBody /> }),
    [panel],
  );
  return useMemo(() => ({ openSection, openArticle, openPrivacy, openTerms }), [openSection, openArticle, openPrivacy, openTerms]);
}

/** Шаги справки там, где она стоит: поле (контекст HelpPane) или боковой слой. */
function useHelpNav(): HelpNav {
  const layer = useLayerNav();
  return useContext(HelpNavContext) ?? layer;
}

/** Клики по ссылкам внутри слоя: /help/<раздел>[/<статья>], /privacy, /oferta — следующий уровень; «#якорь» —
 *  прокрутка к заголовку в теле слоя (смена «#» в адресе положила бы запись истории, и
 *  popstate снял бы уровень). Остальные ссылки (почта, внешние) — как есть. */
function useLayerLinks() {
  const { openSection, openArticle, openPrivacy, openTerms } = useHelpNav();
  return useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a');
      const href = a?.getAttribute('href') ?? '';
      if (!a || !href) return;
      if (href.startsWith('#')) {
        e.preventDefault();
        const id = decodeURIComponent(href.slice(1));
        e.currentTarget.querySelector(`[id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }
      const doc = /^\/(?:[a-z]{2}\/)?(privacy|oferta)\/?(?:\?.*)?$/.exec(href);
      if (doc) {
        e.preventDefault();
        if (doc[1] === 'privacy') openPrivacy();
        else openTerms();
        return;
      }
      const m = /^\/(?:[a-z]{2}\/)?help\/([a-z0-9-]+)(?:\/([a-z0-9-]+))?\/?$/.exec(href);
      if (!m) return;
      e.preventDefault();
      if (m[2]) openArticle(m[1]!, m[2]);
      else openSection(m[1]!);
    },
    [openSection, openArticle, openPrivacy, openTerms],
  );
}

/** Значок раздела справки героем над заголовком (как на страницах /help). */
function Hero({ slug }: { slug: string | null }) {
  return (
    <div className="help-hero">
      <HelpSectionIcon slug={slug} size={80} />
    </div>
  );
}

/** СТРАНИЦА С ГЕРОЕМ СПРАВКИ ВНЕ СПРАВКИ (QUICK-START FIX-3 §7.6, корень «+» «Создать бота»): тот же каркас, что у раздела
 *  справки (`HelpSectionBody`) — значок героем над заголовком, подводка, своё содержимое под ней. Своей вёрстки героя у
 *  хозяина нет; значок — его, цвет значка — роль хозяина (`iconColor`). */
export function HelpHeroPage({
  icon,
  iconColor,
  title,
  lead,
  children,
}: {
  icon: ReactNode;
  iconColor?: string;
  title: string;
  lead?: string;
  children: ReactNode;
}) {
  const locale = useLocale();
  return (
    <div className="help-layer">
      <LegalShell
        locale={locale}
        bare
        className="help"
        title={title}
        above={
          <div className="help-hero" style={iconColor ? { color: iconColor } : undefined}>
            {icon}
          </div>
        }
      >
        {lead ? <p className="help-lead">{lead}</p> : null}
        {children}
      </LegalShell>
    </div>
  );
}

/** Карточки статей раздела справки (название, описание из фронтматтера) — оглавление справки, прогретое на входе
 *  (`help.index`); пока ответа нет — пусто. Нужен экрану «Инструкции» корня «+» (QUICK-START FIX-3 §7.4). */
export function useHelpSectionArticles(slug: string): { slug: string; title: string; description: string }[] {
  const locale = useLocale();
  const { data } = useHelpIndex(locale);
  return (data?.sections?.find((s) => s.slug === slug)?.articles ?? []).map((a) => ({ slug: a.slug, title: a.title, description: a.description ?? '' }));
}

/** Корень справки: разделы одной карточкой и документы второй — как /help на телефоне. */
function HelpIndexBody() {
  const locale = useLocale();
  const t = useTranslations('help');
  const tl = useTranslations('cabinet.settings');
  const { data } = useHelpIndex(locale);
  const { openSection, openArticle, openPrivacy, openTerms } = useHelpNav();
  const onLinks = useLayerLinks();
  // Холодный кэш — разделы из посева сервера (help-seed.tsx, ТЗ WEB-CAUSES М75): карточки есть
  // с первого кадра при любом входе; пришёл ответ /api/help — рисуем его.
  const seed = useHelpSectionSeed(locale);
  const sections = data?.sections ?? seed;
  // Поиск по статьям — только в листе на телефоне (лупа в его шапке); поле широкого экрана
  // (HelpPane) — как было.
  const phoneSheet = useContext(HelpNavContext) === null && !isAppClient() && readLayoutMode() === 'phone';
  return (
    <div className="help-layer" onClick={onLinks}>
      <LegalShell locale={locale} bare className="help" title={t('title')} above={<Hero slug={null} />}>
        <p className="help-lead">{t('lead')}</p>
        {/* Поиск — как на /help: поле раскрывает лупа шапки, со словом разделы прячутся и ищем по
            статьям ниже (строки `data-q`, help.css). */}
        {phoneSheet ? <HelpSearch placeholder={t('search')} emptyTitle={t('searchEmptyTitle')} emptyHint={t('searchEmptyHint')} /> : null}
        <ul className="help-list help-secs">
          {sections.map((s) => (
            <li key={s.slug}>
              <a
                href={`/${locale}/help/${s.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  openSection(s.slug);
                }}
              >
                <span className="help-sec-ic">
                  <HelpSectionIcon slug={s.slug} size={22} />
                </span>
                <span className="help-sec-txt">
                  <span className="help-item-title">{s.title}</span>
                  {s.description ? <span className="help-item-desc">{s.description}</span> : null}
                </span>
              </a>
            </li>
          ))}
        </ul>
        {/* Документы — как на /help: политика и соглашение — следующим уровнем (О5 — политика,
            О6 — соглашение по решению владельца 04.10.2026). */}
        <ul className="help-list help-secs help-legal">
          <li>
            <a
              href={`/${locale}/privacy?from=help`}
              onClick={(e) => {
                e.preventDefault();
                openPrivacy();
              }}
            >
              <span className="help-sec-ic">
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M12 3 5 6v5.5c0 4.3 3 8 7 9.5 4-1.5 7-5.2 7-9.5V6z" />
                  <path d="m9 12 2.2 2.2L15.5 10" />
                </svg>
              </span>
              <span className="help-sec-txt">
                <span className="help-item-title">{tl('privacyRow')}</span>
              </span>
            </a>
          </li>
          <li>
            <a
              href={`/${locale}/oferta?from=help`}
              onClick={(e) => {
                e.preventDefault();
                openTerms();
              }}
            >
              <span className="help-sec-ic">
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
                  <path d="M14 3v4h4M9 12h6M9 16h6" />
                </svg>
              </span>
              <span className="help-sec-txt">
                <span className="help-item-title">{tl('termsRow')}</span>
              </span>
            </a>
          </li>
        </ul>
        {(phoneSheet ? (data?.sections ?? []) : []).map((s) => (
          <section key={s.slug} className="help-sec">
            <h2>{s.title}</h2>
            <ul className="help-list">
              {s.articles.map((a) => (
                <li key={a.slug} data-q={`${a.title} ${a.description ?? ''} ${s.title}`.toLowerCase()}>
                  <a
                    href={`/${locale}/help/${s.slug}/${a.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      openArticle(s.slug, a.slug);
                    }}
                  >
                    <span className="help-item-title">{a.title}</span>
                    {a.description ? <span className="help-item-desc">{a.description}</span> : null}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {/* ПОИСК ИЩЕТ И ПО ДОКУМЕНТАМ (APP-CLAIMS-1020 ред. 6, П26.4) — та же группа «Документы» со строками `data-q`, что на
            /help: видна только при поиске (help.css), тап — следующим уровнем (useLayerLinks). */}
        {phoneSheet ? (
          <section className="help-sec help-docs">
            <h2>{t('docsTitle')}</h2>
            <ul className="help-list">
              <li data-q={`${tl('privacyRow')} ${t('docsTitle')}`.toLowerCase()}>
                <a href={`/${locale}/privacy?from=help`}>
                  <span className="help-item-title">{tl('privacyRow')}</span>
                </a>
              </li>
              <li data-q={`${tl('termsRow')} ${t('docsTitle')}`.toLowerCase()}>
                <a href={`/${locale}/oferta?from=help`}>
                  <span className="help-item-title">{tl('termsRow')}</span>
                </a>
              </li>
            </ul>
          </section>
        ) : null}
      </LegalShell>
    </div>
  );
}

/** Раздел справки: значок, название, подводка и карточка статей. */
function HelpSectionBody({ slug }: { slug: string }) {
  const locale = useLocale();
  const { data } = useHelpIndex(locale);
  const { openArticle } = useHelpNav();
  const onLinks = useLayerLinks();
  const sec = data?.sections?.find((s) => s.slug === slug);
  // Форма списка статей раздела (ред. 9, 7.4.2) — снять при первом показе с данными; пока данных нет — заглушка формы.
  useShapeRecord('help-section', !!sec && (sec.articles ?? []).length > 0);
  // Тела статей раздела уже в оглавлении (lib/help-article-data.ts) — отдельного прогрева статей нет.
  return (
    <div className="help-layer" onClick={onLinks}>
      <LegalShell locale={locale} bare className="help" title={sec?.title ?? ''} above={<Hero slug={slug} />}>
        {sec?.description ? <p className="help-lead">{sec.description}</p> : null}
        {!sec ? <Shape name="help-section" groupsClass="" groupClass="help-list" /> : null}
        <ul className="help-list" data-shape="help-section">
          {(sec?.articles ?? []).map((a) => (
            <li key={a.slug}>
              <a
                href={`/${locale}/help/${a.section}/${a.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  openArticle(a.section, a.slug);
                }}
              >
                <span className="help-item-title">{a.title}</span>
                {a.description ? <span className="help-item-desc">{a.description}</span> : null}
              </a>
            </li>
          ))}
        </ul>
      </LegalShell>
    </div>
  );
}

/** «Поделиться» статьёй — кружок в шапке слоя (как на странице статьи на телефоне). */
function HelpArticleShare({ section, slug, roundClass }: { section: string; slug: string; roundClass?: string }) {
  const locale = useLocale();
  const t = useTranslations('help');
  const { data } = useHelpIndex(locale);
  const art = articleOf(data, section, slug);
  return (
    <HelpShare
      path={`/${locale}/help/${section}/${slug}`}
      label={t('share')}
      copied={t('shareCopied')}
      title={art ? articleTitles(art).list : ''}
      round
      {...(roundClass ? { roundClass } : {})}
    />
  );
}

/** ОКНО СТАТЬИ — ОДНО НА ВСЕ ВХОДЫ КАБИНЕТА (ТЗ HELP-ARTICLE-UNIFY §5.2, владелец 25.10.2026): справка, пасхалка «Ботов»,
 *  «Создать бота» → «Пошаговые инструкции», ссылки внутри статей. Стопка снизу на телефоне, панель 460 справа на широком
 *  экране (панель = телефон); шапка — «× · короткий заголовок (`articleTitles().bar`) · поделиться» на любой ширине.
 *  Страница под окном не меняется: корень справки не открывается, адрес пишется, только если его дал вход (`href` —
 *  уровень справки; из «Ботов» и каталога адреса нет). «×» снимает только окно. */
function openArticleSheet(
  panel: ReturnType<typeof useSlidePanel>,
  locale: string,
  at: { section: string; slug: string; href?: string; closeHref?: string },
  tries = 0,
): void {
  const { section, slug } = at;
  const titles = cardTitles(locale, section, slug);
  // Оглавления ещё нет (прямой заход по адресу статьи, первые секунды кабинета) — окно ждёт его (запрос уже в пути у
  // прогрева, ответ ложится в тот же кэш), чтобы встать сразу с шапкой и текстом (закон «экран показывается один раз»).
  if (!titles) {
    void awaitPersistent<HelpIndexReply>(helpIndexKey(locale), helpIndexUrl(locale), helpIndexOpts).then((idx) => {
      const art = articleOf(idx, section, slug);
      if (art) showArticleSheet(panel, locale, at, articleTitles(art).bar);
      else if (tries < 40) window.setTimeout(() => openArticleSheet(panel, locale, at, tries + 1), 250);
    });
    return;
  }
  showArticleSheet(panel, locale, at, titles.bar);
}

function showArticleSheet(
  panel: ReturnType<typeof useSlidePanel>,
  locale: string,
  at: { section: string; slug: string; href?: string; closeHref?: string },
  bar: string,
): void {
  const { section, slug } = at;
  panel.open({
    title: bar,
    sheet: true,
    deck: true,
    lead: 'close',
    winKey: 'help-art',
    ...(at.href ? { href: at.href } : {}),
    ...(at.closeHref ? { closeHref: at.closeHref } : {}),
    headerRight: <HelpArticleShare section={section} slug={slug} roundClass="acctbtn" />,
    body: <HelpArticleSheet section={section} slug={slug} />,
  });
}

/** Тело окна статьи: общий компонент + ссылки внутри статьи следующим окном. */
function HelpArticleSheet({ section, slug }: { section: string; slug: string }) {
  const onLinks = useLayerLinks();
  return <HelpArticle place="sheet" section={section} slug={slug} onLinks={onLinks} />;
}

/** Открыть статью справки окном поверх ТЕКУЩЕГО места (каталог «Создать бота», пасхалка «Ботов»): без корня справки,
 *  без смены адреса страницы под окном. */
export function useOpenHelpArticle(): (section: string, slug: string) => void {
  const panel = useSlidePanel();
  const locale = useLocale();
  return useCallback((section: string, slug: string) => openArticleSheet(panel, locale, { section, slug }), [panel, locale]);
}

/** Открыть РАЗДЕЛ справки шагом вбок поверх ТЕКУЩЕГО окна (QUICK-START FIX-2 §6.3, дверь 2 «Хочу разобраться» корня «+»):
 *  та же страница раздела, что в справке, без корня справки и без смены адреса; «‹» на телефоне — пилюлей со словом того
 *  места, куда вернёт (`back`). Статьи раздела — окном-стопкой, как из каталога и пасхалки (без адреса). */
export function useOpenHelpSectionHere(): (slug: string, back: string) => void {
  const panel = useSlidePanel();
  const locale = useLocale();
  const layer = useLayerNav();
  return useCallback(
    (slug: string, back: string) => {
      const phone = !isAppClient() && readLayoutMode() === 'phone';
      const nav: HelpNav = { ...layer, openArticle: (section, art) => openArticleSheet(panel, locale, { section, slug: art }) };
      panel.open({
        title: '',
        ...helpDir('page'),
        lead: 'back',
        ...(phone ? { headerLeft: <HelpBackSlot label={back} onBack={() => panel.close()} /> } : {}),
        winKey: 'help-sec',
        body: (
          <HelpNavContext.Provider value={nav}>
            <HelpSectionBody slug={slug} />
          </HelpNavContext.Provider>
        ),
      });
    },
    [panel, locale, layer],
  );
}

/** Политика конфиденциальности слоем: тот же текст, что на /privacy (privacy-text.tsx). */
function PrivacyBody() {
  const locale = useLocale();
  const head = privacyHead(locale);
  return (
    <div className="help-layer">
      <LegalShell locale={locale} bare title={head.title} updated={head.updated}>
        <PrivacyText locale={locale} />
      </LegalShell>
    </div>
  );
}

/** Пользовательское соглашение слоем: тот же текст, что на /oferta (terms-text.tsx). */
function TermsBody() {
  const locale = useLocale();
  const head = termsHead(locale);
  return (
    <div className="help-layer">
      <LegalShell locale={locale} bare title={head.title} updated={head.updated}>
        <TermsText locale={locale} />
      </LegalShell>
    </div>
  );
}

/** Соглашение слоем с прокруткой к пункту (`#autorenew` — строка согласия под «Автопродлением» экрана
 *  расчёта, PAY-FLOW-TZ §8 п.10.9): тот же текст, что на /oferta и в уровне справки. */
export function TermsLayerAt({ anchor }: { anchor?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Без пункта (оферта целиком, строка под кнопкой оплаты без автопродления, §18.1 п.5) — с начала.
    if (!anchor) return;
    // Лист въезжает с анимацией — прокрутка после первого кадра, когда тело уже в своей прокрутке.
    const id = window.requestAnimationFrame(() =>
      // Посередине, не к верху: верх тела листа закрыт стеклянной шапкой.
      ref.current?.querySelector(`[id="${CSS.escape(anchor)}"]`)?.scrollIntoView({ block: 'center' }),
    );
    return () => window.cancelAnimationFrame(id);
  }, [anchor]);
  return (
    <div ref={ref}>
      <TermsBody />
    </div>
  );
}

/** Открыть справку слоем. Прямой заход по /app/account/help/<раздел>[/<статья>] (и
 *  …/help/privacy, …/help/terms) — сразу с тем же уровнем поверх корня: адрес захода заменяется
 *  адресом корня, уровень кладёт свою запись — «назад» снимает его, а не уводит из кабинета.
 *  `at` — тот же хвост адреса, когда уровень просят не адресом (строки документов). */
export function useOpenHelpLayer(): (at?: string) => void {
  const panel = useSlidePanel();
  const { openSection, openArticle, openPrivacy, openTerms } = useLayerNav();
  const tSearch = useTranslations('help');
  // ОГЛАВЛЕНИЕ — ЗАРАНЕЕ (пакет М4, приёмка 04.10.2026): лист открывается уже с ним — его греет вход в кабинет (цель
  // `help.index`, У1 плана прогрева), своего прогрева у входа в справку больше нет (ТЗ HELP-WARM-FIX §2).
  return useCallback(
    (at?: string) => {
      const here = typeof window === 'undefined' ? '' : window.location.pathname.replace(/^\/[a-z]{2}(?=\/)/, '');
      const tail = at ?? (here.startsWith(`${HELP_BASE}/`) ? here.slice(HELP_BASE.length + 1) : '');
      const [sec, art] = tail ? tail.split('/') : [];
      const deep = !!sec && /^[a-z0-9-]+$/.test(sec);
      if (deep && at === undefined) window.history.replaceState(null, '', HELP_BASE);
      const phone = !isAppClient() && readLayoutMode() === 'phone';
      panel.open({
        title: '',
        ...helpDir('page'),
        // Корень из «Аккаунта» — шаг вбок со скобкой «‹» (10.2; iOS: push из «Аккаунта»), не окно с «×».
        ...(phone ? { lead: 'back' as const } : {}),
        // Кружок-лупа справа в шапке корня, как у iOS (кадр K17-help-sections): раскрывает поле
        // поиска в теле (HelpSearch, событие документа). Только телефон — широкий не трогаем.
        ...(phone ? { headerRight: <HelpSearchButton className="dr-info" label={tSearch('search')} onClick={askHelpSearch} /> } : {}),
        winKey: 'help',
        href: HELP_BASE,
        closeHref: pickToPath('account', ''),
        body: <HelpIndexBody />,
      });
      if (!deep) return;
      // Уровень — следующим кадром: стопка кладёт его поверх уже открытого корня.
      window.requestAnimationFrame(() => {
        if (sec === 'privacy' && !art) openPrivacy();
        else if (sec === 'terms' && !art) openTerms();
        else if (art && /^[a-z0-9-]+$/.test(art)) openArticle(sec!, art);
        else openSection(sec!);
      });
    },
    [panel, openSection, openArticle, openPrivacy, openTerms, tSearch],
  );
}

/** Уровень справки по хвосту адреса /app/account/help/…: [] — корень, [privacy] / [terms] —
 *  документ, [раздел] — раздел, [раздел, статья] — статья. */
type HelpLevel =
  | { kind: 'index' }
  | { kind: 'privacy' }
  | { kind: 'terms' }
  | { kind: 'section'; section: string }
  | { kind: 'article'; section: string; slug: string };

function levelOf(pathname: string): HelpLevel {
  const [, sec, art] = tailOf('account', pathname);
  const ok = (v: string | undefined): v is string => !!v && /^[a-z0-9-]+$/.test(v);
  if (!ok(sec)) return { kind: 'index' };
  if (!art && (sec === 'privacy' || sec === 'terms')) return { kind: sec };
  if (ok(art)) return { kind: 'article', section: sec, slug: art };
  return { kind: 'section', section: sec };
}

/** Адрес уровня на шаг выше: статья → раздел, раздел и документ → корень справки. */
function parentHref(_level: HelpLevel): string {
  // Статья стоит окном над своим разделом — «‹» поля и у раздела, и под статьёй ведёт в корень.
  return HELP_BASE;
}

/** Сколько шагов вглубь положило в историю САМО поле: «‹» снимает их шагом истории назад, а при
 *  прямом заходе по адресу (записи нет) — тихой заменой адреса на уровень выше, чтобы «‹» не
 *  вынес человека из кабинета. Общая на поле (HelpPane и его «‹» в шапке). */
const panePushes = { n: 0 };

/** СПРАВКА В ПРАВОМ ПОЛЕ «АККАУНТА» (широкий экран, ТЗ ANIM-FIX О6): те же тела, что у бокового
 *  слоя, уровень — из адреса. Шаг вглубь — тихий pushState (Next сам сверяет usePathname, дерево
 *  кабинета не пересобирается — тот же способ, что у вкладок, shell.tsx navTo). */
export function HelpPane() {
  const pathname = usePathname() ?? '';
  const level = levelOf(pathname);
  const panel = useSlidePanel();
  const locale = useLocale();
  const openedHere = useRef('');
  const go = useCallback((href: string) => {
    panePushes.n += 1;
    window.history.pushState(null, '', href);
  }, []);
  const nav = useMemo<HelpNav>(
    () => ({
      openSection: (slug) => go(secHref(slug)),
      // Статья — НЕ в поле, а окном-панелью 460 справа (В1: панель = телефон); поле остаётся разделом.
      openArticle: (section, slug) => {
        openedHere.current = `${section}/${slug}`;
        openArticleSheet(panel, locale, { section, slug, href: artHref(section, slug) });
      },
      openPrivacy: () => go(`${HELP_BASE}/privacy`),
      openTerms: () => go(`${HELP_BASE}/terms`),
    }),
    [go, panel, locale],
  );
  // ПРЯМОЙ ЗАХОД НА АДРЕС СТАТЬИ (ссылка, перезагрузка): поле — её раздел, статья — окном поверх. Окно, открытое строкой
  // раздела, само кладёт этот адрес — второй раз его не открываем.
  const artKey = level.kind === 'article' ? `${level.section}/${level.slug}` : '';
  useEffect(() => {
    if (!artKey || openedHere.current === artKey) return;
    openedHere.current = artKey;
    const [section, slug] = artKey.split('/');
    openArticleSheet(panel, locale, { section: section!, slug: slug!, href: artHref(section!, slug!), closeHref: secHref(section!) });
  }, [artKey, panel, locale]);
  useEffect(() => {
    if (!artKey) openedHere.current = '';
  }, [artKey]);
  // Новый уровень — с начала: без этого статья открывалась бы на той высоте, где человек нажал
  // строку раздела. Прокручивается поле (ближний прокручиваемый предок) — его и сбрасываем.
  const boxRef = useRef<HTMLDivElement | null>(null);
  const firstRef = useRef(true);
  // Под окном статьи поле стоит на её разделе — прокрутка сбрасывается по смене РАЗДЕЛА, не статьи.
  const scrollKey = level.kind === 'article' ? secHref(level.section) : pathname;
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    for (let el = boxRef.current?.parentElement ?? null; el; el = el.parentElement) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
        el.scrollTop = 0;
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [scrollKey]);
  // Ушли с поля справки (другой пункт колонки) — счёт шагов поля обнуляется.
  useEffect(
    () => () => {
      panePushes.n = 0;
    },
    [],
  );
  const body =
    level.kind === 'index' ? (
      <HelpIndexBody />
    ) : level.kind === 'privacy' ? (
      <PrivacyBody />
    ) : level.kind === 'terms' ? (
      <TermsBody />
    ) : level.kind === 'section' ? (
      <HelpSectionBody key={level.section} slug={level.section} />
    ) : (
      <HelpSectionBody key={level.section} slug={level.section} />
    );
  return (
    <HelpNavContext.Provider value={nav}>
      <div ref={boxRef}>{body}</div>
    </HelpNavContext.Provider>
  );
}

/** Ведущий кружок шапки поля справки: на корне — `root` (кружок темы, как у прочих страниц
 *  «Аккаунта»), глубже — «‹» на уровень выше. */
export function HelpPaneLead({ root }: { root: React.ReactNode }) {
  const pathname = usePathname() ?? '';
  const level = levelOf(pathname);
  const t = useTranslations('cabinet');
  if (level.kind === 'index') return <>{root}</>;
  const back = () => {
    if (panePushes.n > 0) {
      panePushes.n -= 1;
      window.history.back();
      return;
    }
    window.history.replaceState(null, '', parentHref(level));
  };
  return (
    <KBareButton className="acctbtn" aria-label={t('back')} title={t('back')} onClick={back}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M15 5l-7 7 7 7" />
      </svg>
    </KBareButton>
  );
}
